import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Filter, X, Calendar, Globe, Layers, Radio } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export interface FilterState {
  period: string
  country: string
  cluster: string
  source: string
  status: string
}

const DEFAULTS: FilterState = {
  period: '30d',
  country: '',
  cluster: '',
  source: '',
  status: '',
}

const periods = [
  { value: '7d', label: '7 jours' },
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
  { value: '12m', label: '12 mois' },
]

interface FilterBarProps {
  countries?: string[]
  clusters?: string[]
  onFilterChange: (filters: FilterState) => void
}

export function FilterBar({ countries = [], clusters = [], onFilterChange }: FilterBarProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [expanded, setExpanded] = useState(false)

  // Init from URL
  const [filters, setFilters] = useState<FilterState>(() => ({
    period: searchParams.get('period') || DEFAULTS.period,
    country: searchParams.get('country') || DEFAULTS.country,
    cluster: searchParams.get('cluster') || DEFAULTS.cluster,
    source: searchParams.get('source') || DEFAULTS.source,
    status: searchParams.get('status') || DEFAULTS.status,
  }))

  // Sync to URL + debounced callback
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== DEFAULTS[k as keyof FilterState]) params.set(k, v)
      })
      setSearchParams(params, { replace: true })
      onFilterChange(filters)
    }, 300)
    return () => clearTimeout(timer)
  }, [filters])

  const activeCount = Object.entries(filters).filter(
    ([k, v]) => v && v !== DEFAULTS[k as keyof FilterState]
  ).length

  const handleChange = useCallback((key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleReset = () => setFilters(DEFAULTS)

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-ink-border -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <div className="flex items-center gap-3 py-2.5">
        {/* Period chips — always visible */}
        <div className="flex items-center gap-1">
          {periods.map((p) => (
            <button
              key={p.value}
              onClick={() => handleChange('period', p.value)}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                filters.period === p.value
                  ? 'bg-brick text-white'
                  : 'bg-paper-warm text-ink hover:bg-paper-warm',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-paper-warm" />

        {/* Toggle advanced filters */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
            expanded ? 'bg-gold-light text-brick' : 'bg-paper-warm text-ink hover:bg-paper-warm',
          )}
        >
          <Filter size={14} />
          Filtres
          {activeCount > 0 && (
            <span className="ml-1 w-5 h-5 rounded-full bg-brick text-white text-[10px] flex items-center justify-center font-bold">
              {activeCount}
            </span>
          )}
        </button>

        {/* Reset */}
        {activeCount > 0 && (
          <button
            onClick={handleReset}
            className="flex items-center gap-1 text-xs text-ink-faint hover:text-ink"
          >
            <X size={12} />
            Réinitialiser
          </button>
        )}
      </div>

      {/* Advanced filters — collapsible */}
      {expanded && (
        <div className="flex items-center gap-3 pb-3 animate-slide-down">
          {/* Country */}
          {countries.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Globe size={14} className="text-ink-faint" />
              <select
                value={filters.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className="text-xs border border-ink-border rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-gold focus:outline-none"
              >
                <option value="">Tous les pays</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          {/* Cluster */}
          {clusters.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Layers size={14} className="text-ink-faint" />
              <select
                value={filters.cluster}
                onChange={(e) => handleChange('cluster', e.target.value)}
                className="text-xs border border-ink-border rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-gold focus:outline-none"
              >
                <option value="">Tous les clusters</option>
                {clusters.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          )}

          {/* Source */}
          <div className="flex items-center gap-1.5">
            <Radio size={14} className="text-ink-faint" />
            <select
              value={filters.source}
              onChange={(e) => handleChange('source', e.target.value)}
              className="text-xs border border-ink-border rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-gold focus:outline-none"
            >
              <option value="">Toutes les sources</option>
              <option value="google_ads">Google Ads</option>
              <option value="keyword_planner">Keyword Planner</option>
              <option value="search_console">Search Console</option>
            </select>
          </div>

          {/* Status */}
          <div className="flex items-center gap-1">
            {['active', 'syncing', 'error', 'draft'].map((s) => (
              <button
                key={s}
                onClick={() => handleChange('status', filters.status === s ? '' : s)}
                className={cn(
                  'px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors',
                  filters.status === s
                    ? 'bg-brick text-white'
                    : 'bg-paper-warm text-ink-muted hover:bg-paper-warm',
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
